import React, { useEffect, useState } from "react";
import type { AnswerState, MatchWordsExercise } from "../../types/exercises";
import { ChoiceTile, TileState } from "./ChoiceTile";
import { shuffle } from "../../utils/text";
import { speak } from "../../utils/speech";
interface MatchWordsProps {
  exercise: MatchWordsExercise;
  onAnswer: (a: AnswerState) => void;
  speechLang: string;
}
export function MatchWords({
  exercise,
  onAnswer,
  speechLang
}: MatchWordsProps) {
  const [right] = useState(() => shuffle(exercise.pairs.map(p => p.translation)));
  const [left, setLeft] = useState<string | null>(null);
  const [rightSel, setRightSel] = useState<string | null>(null);
  const [matched, setMatched] = useState<Set<string>>(new Set());
  const [flash, setFlash] = useState<{
    terms: string[];
    kind: "correct" | "wrong";
  } | null>(null);
  useEffect(() => {
    if (!left || !rightSel) return;
    const pair = exercise.pairs.find(p => p.term === left);
    const ok = pair?.translation === rightSel;
    setFlash({
      terms: [left, rightSel],
      kind: ok ? "correct" : "wrong"
    });
    const t = window.setTimeout(() => {
      if (ok) setMatched(m => new Set(m).add(left));
      setFlash(null);
      setLeft(null);
      setRightSel(null);
    }, ok ? 320 : 420);
    return () => window.clearTimeout(t);
  }, [left, rightSel, exercise.pairs]);
  useEffect(() => {
    if (matched.size === exercise.pairs.length) {
      onAnswer({
        ready: true,
        correct: true,
        auto: true
      });
    }
  }, [matched, exercise.pairs.length, onAnswer]);
  const matchedTranslations = new Set(exercise.pairs.filter(p => matched.has(p.term)).map(p => p.translation));
  const stateFor = (value: string, side: "l" | "r"): TileState => {
    if (flash?.terms.includes(value)) return flash.kind;
    if (side === "l" ? matched.has(value) : matchedTranslations.has(value)) return "matched";
    if ((side === "l" ? left : rightSel) === value) return "selected";
    return "idle";
  };
  return <div className="grid grid-cols-2 gap-3 md:gap-4" data-id="element-384" data-mp="21a7vy13fo7db">
      <ul className="flex flex-col gap-3" aria-label="Words" data-id="element-385" data-mp="218wq7vqfgpjk">
        {exercise.pairs.map(p => <li key={p.term} data-id="element-386" data-mp="1w9k81oai46nb">
            <ChoiceTile state={stateFor(p.term, "l")} lang="es" className="w-full" disabled={Boolean(flash)} onClick={() => {
          setLeft(p.term);
          speak(p.term, speechLang);
        }} data-id="element-387" data-mp="2b09lyyfcjzuv">
              {p.term}
              {p.fromTranslator && <span className="absolute -right-1.5 -top-2 h-3.5 w-3.5 rounded-full border-2 border-line bg-accent" title="From your translator" data-id="element-388" data-mp="1delrij2gbrlz" />}
            </ChoiceTile>
          </li>)}
      </ul>
      <ul className="flex flex-col gap-3" aria-label="Translations" data-id="element-389" data-mp="33p6re0ygm267">
        {right.map(t => <li key={t} data-id="element-390" data-mp="2yq3xncnxjidj">
            <ChoiceTile state={stateFor(t, "r")} className="w-full" disabled={Boolean(flash)} onClick={() => setRightSel(t)} data-id="element-391" data-mp="2yqn4nyc1kmcs">
              {t}
            </ChoiceTile>
          </li>)}
      </ul>
    </div>;
}